import { defineComponent as _defineComponent } from 'vue'
import { orderBy } from 'lodash';
import IconSorting from '@/ui/icons/IconSorting.vue';
import SalesDocumentTableMobileOverlay from './SalesDocumentTableMobileOverlay.vue';
import { searchStringService } from '@/ui/views/salesdocumenttable/searchstring.service';
import QueryParamsService, { QueryParams } from '@/core/query-params.service';
import {
    ISorting, SalesDocumentTableColumn,
    SalesDocumentTableConfig,
    SortingDirection
} from '@/ui/views/salesdocumenttable/salesdocument.types';
import Http from '@/core/http/http.service';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import { watchRelativeUrl } from '@/core/route.service';


export default /*#__PURE__*/_defineComponent({
  __name: 'SalesDocumentTable',
  props: {
    config: null,
    data: null,
    tableClass: null,
    queryParamKey: { default: 'expand' },
    filterValue: { default: '' },
    recursiveIndex: { default: 0 }
  },
  emits: ["onListImageZoom", "onFilter"],
  setup(__props: any, { emit }: { emit: ({(e: 'onListImageZoom', images: CarouselImageViewObject[]): void, (e: 'onFilter', filterValue: string, length: number): void }), expose: any, slots: any, attrs: any }) {

const props = __props as { config: SalesDocumentTableConfig, data: any[], tableClass: string, queryParamKey: string, filterValue: string, recursiveIndex: number }





const salesInfoElement = ref(null);
const activeSort = ref<ISorting>();

const salesInfos: Map<string, SalesInfoViewObject> = new Map();
const salesInfoVariantId = ref<string>(null);
const salesInfosUpdates = ref<number>(0);

const nextRecursiveIndex = computed<number>(() => props.recursiveIndex + 1);

onMounted(() => {
    activeSort.value = props.config.defaultSort;
    if (!activeSort.value) {
        return;
    }

    const initialSortValue = QueryParamsService.getParam(QueryParams.ClientSortValue);
    const initialSortDirection = QueryParamsService.getParam(QueryParams.ClientSortDirection);

    if (initialSortValue) {
        activeSort.value.sortBy = initialSortValue;
    }

    if (initialSortDirection) {
        activeSort.value.direction = (initialSortDirection as SortingDirection);
    }
    expandedStates.value = getExpandedStates();
});

watchRelativeUrl(onRelativeUrlChange);

function onRelativeUrlChange(route: RelativeUrl, oldRoute: RelativeUrl) {
    const triggerChange = route && oldRoute && route.path === oldRoute.path;
    if (!triggerChange) {
        return;
    }

    // Don't update result when navigating away from result page
    const sortQueries = [QueryParams.ClientSortValue, QueryParams.ClientSortDirection];
    const changedQueries = QueryParamsService.getChangedQueries(route, oldRoute);
    const hasSortQueriesChanged = QueryParamsService.hasQueryChanged(sortQueries, changedQueries);

    if (hasSortQueriesChanged) {
        activeSort.value = {
            sortBy: QueryParamsService.getParam(QueryParams.ClientSortValue),
            direction: (QueryParamsService.getParam(QueryParams.ClientSortDirection) as SortingDirection)
        };
    }
}

const expandedStates = ref<string[]>([]);

function sortInvoice(sortField) {
    if (!sortField) { return; }

    let direction: SortingDirection = 'asc';
    if (activeSort.value.sortBy === sortField) {
        direction = activeSort.value.direction === 'asc' ? 'desc' : 'asc';
    }
    updateSortParams(sortField, direction);
}

const router = useRouter();

function updateSortParams(sortBy:string, direction: SortingDirection):void {
    const sortParams: Array<{name: string, value?: string}> = [
        {
            name: QueryParams.ClientSortValue,
            value: sortBy
        },
        {
            name: QueryParams.ClientSortDirection,
            value: direction
        }
    ];
    const query = QueryParamsService.appendQueryParams(sortParams);
    router.replace({ query });
}

const searchableRows = computed<any[]>(() => {
    if (props.config.filterFields) {
        return props.data.map(row => {
            return { ...row, searchString: searchStringService.createSearchString(row, props.config.filterFields) };
        });
    }

    return props.data;
});

const filteredAndSortedRows = computed<any[]>(() => {
    let searchableRowsValue = searchableRows.value;

    if (props.config.filterFields) {
        searchableRowsValue = searchableRowsValue.filter(row => row.searchString.includes(props.filterValue.toLowerCase()));
    }

    if (activeSort.value) {
        searchableRowsValue = orderBy([...searchableRowsValue], [x => (x[activeSort.value.sortBy] || 'undefined').toLowerCase()], activeSort.value.direction);
    }

    emit('onFilter', props.filterValue, searchableRowsValue.length);
    return searchableRowsValue;
});

function isExpanded(row: any): boolean {
    const key = row[props.config.rowKey];
    return expandedStates.value.indexOf(key) > -1;
}

function getExpandedStates(): string[] {
    const query = QueryParamsService.getParam(props.queryParamKey);
    if (!query) { return []; }
    return query.split(',');
}

function expand(row: any, column: SalesDocumentTableColumn): void {
    if (column && !shouldCellExpand(column)) {
        return;
    }

    const key = row[props.config.rowKey];

    const index = expandedStates.value.indexOf(key);
    if (index > -1) {
        expandedStates.value.splice(index, 1);
    } else {
        expandedStates.value.push(key);
    }

    const query = { ...router.currentRoute.query };
    query[props.queryParamKey] = expandedStates.value.length ? expandedStates.value.join(',') : undefined;
    router.replace({ query });
}

function salesInfoUpdate(evt: MouseEvent, row: any, column: SalesDocumentTableColumn): void {
    if (!column.salesInfoField) { return; }

    salesInfoVariantId.value = row[column.salesInfoField];

    const target = evt.currentTarget as HTMLElement;
    salesInfoElement.value.style.top = target.offsetTop + 'px';
    salesInfoElement.value.style.left = target.offsetLeft + 'px';

    const hashCached = salesInfos.has(salesInfoVariantId.value);
    if (hashCached) {
        return;
    }

    salesInfos.set(salesInfoVariantId.value, null);

    const request: SalesInfoWebRequest = {
        sapIds: [salesInfoVariantId.value]
    };

    Http.post('salesinfo/salesinfo', request).then((response: SalesInfoViewModel) => {
        response.variants.forEach((salesInfo: SalesInfoViewObject) => {
            salesInfos.set(salesInfo.variantId, salesInfo);
            salesInfosUpdates.value++;
        });
    });
}

const activeSaleInfo = computed(() => {
    if (salesInfosUpdates.value) { return salesInfos.get(salesInfoVariantId.value); }

    return null;
});

function salesInfoHide() {
    salesInfoVariantId.value = null;
}

function isSortingByColumn(column: SalesDocumentTableColumn): boolean {
    if (!activeSort.value || !column.sortField) { return false; }

    return activeSort.value.sortBy === column.sortField;
}

function columnHasSort(column: SalesDocumentTableColumn): boolean {
    return !!column.sortField;
}

function shouldCellExpand(column: SalesDocumentTableColumn): boolean {
    return (props.config.subTableConfig && !column.linkField);
}

function isContentVisible(row: any, column: SalesDocumentTableColumn): boolean {
    if (!column.hideContentField) { return true; }

    return !!row[column.hideContentField];
}

function onListImageZoom(images: CarouselImageViewObject[]):void {
    emit('onListImageZoom', images);
}

// animation
function beforeEnter(el) { el.style.height = '0'; }
function enter(el) { el.style.height = el.scrollHeight + 'px'; }
function beforeLeave(el) { el.style.height = el.scrollHeight + 'px'; }
function leave(el) { el.style.height = '0'; }


return { __sfc: true,props, emit, salesInfoElement, activeSort, salesInfos, salesInfoVariantId, salesInfosUpdates, nextRecursiveIndex, onRelativeUrlChange, expandedStates, sortInvoice, router, updateSortParams, searchableRows, filteredAndSortedRows, isExpanded, getExpandedStates, expand, salesInfoUpdate, activeSaleInfo, salesInfoHide, isSortingByColumn, columnHasSort, shouldCellExpand, isContentVisible, onListImageZoom, beforeEnter, enter, beforeLeave, leave, IconSorting, SalesDocumentTableMobileOverlay }
}

})